import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import './styles/variables.css';

// Context
import { AuthProvider, useAuth } from './context/AuthContext';
import { SocketProvider } from './context/SocketContext';
import { WishlistProvider } from './context/WishlistContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { SettingsProvider } from './context/SettingsContext';

// Eager-loaded (critical path)
import Home from './pages/Home';
import Login from './pages/Auth/Login';
import Navbar from './components/Navbar/Navbar';
import ScrollToTop from './components/ScrollToTop';
import ProtectedRoute from './components/ProtectedRoute';
import { getPortalMode } from './config/portal';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import NotFound from './pages/NotFound/NotFound';

// Lazy customer pages
const Shop = lazy(() => import('./pages/Shop/Shop'));
const Plan = lazy(() => import('./pages/Plan/Plan'));
const Dashboard = lazy(() => import('./pages/Dashboard/Dashboard'));
const Signup = lazy(() => import('./pages/Auth/Signup'));
const ForgotPassword = lazy(() => import('./pages/Auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/Auth/ResetPassword'));
const ServiceDetail = lazy(() => import('./pages/Services/ServiceDetail'));

// Lazy admin pages
const AdminDashboard = lazy(() => import('./pages/Admin/AdminDashboard'));
const UsersManagement = lazy(() => import('./pages/Admin/UsersManagement'));
const ProjectsManagement = lazy(() => import('./pages/Admin/ProjectsManagement'));
const QuotesManagement = lazy(() => import('./pages/Admin/QuotesManagement'));
const OrdersManagement = lazy(() => import('./pages/Admin/OrdersManagement'));
const ProductsManagement = lazy(() => import('./pages/Admin/ProductsManagement'));
const InventoryManagement = lazy(() => import('./pages/Admin/InventoryManagement'));
const Analytics = lazy(() => import('./pages/Admin/Analytics'));
const CategoriesManagement = lazy(() => import('./pages/Admin/CategoriesManagement'));
const BannersManagement = lazy(() => import('./pages/Admin/BannersManagement'));
const SettingsManagement = lazy(() => import('./pages/Admin/SettingsManagement'));
const ConsultationsManagement = lazy(() => import('./pages/Admin/ConsultationsManagement'));

// Lazy payment + notification pages
const PaymentPage = lazy(() => import('./pages/Payment/PaymentPage'));
const GuestPaymentPage = lazy(() => import('./pages/Payment/GuestPaymentPage'));
const PaymentCallback = lazy(() => import('./pages/Payment/PaymentCallback'));
const NotificationsPage = lazy(() => import('./pages/Notifications/NotificationsPage'));

// Lazy floating UI
const WhatsAppButton = lazy(() => import('./components/WhatsAppButton/WhatsAppButton'));

const RouteLoader = () => (
  <div
    style={{
      minHeight: '60vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
    aria-label="Loading page"
  >
    <div
      style={{
        width: 28,
        height: 28,
        border: '3px solid rgba(255,107,53,0.2)',
        borderTopColor: '#ff6b35',
        borderRadius: '50%',
        animation: 'lb-spin 0.8s linear infinite',
      }}
    />
    <style>{`@keyframes lb-spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

// Theme applier - applies dark mode only on supported routes
const ThemeApplier = () => {
  const location = useLocation();
  const { applyThemeForRoute } = useTheme();
  
  React.useEffect(() => {
    applyThemeForRoute(location.pathname);
  }, [location.pathname, applyThemeForRoute]);
  
  return null;
};

// Layout wrapper to conditionally show navbar
const AppLayout = ({ children }) => {
  const location = useLocation();
  const portalMode = getPortalMode();
  const isAdminRoute = location.pathname.startsWith('/admin') || portalMode === 'admin';
  
  return (
    <div className="App">
      {!isAdminRoute && <Navbar />}
      {children}
      {/* WhatsApp floating button - appears on all pages except admin */}
      {!isAdminRoute && (
        <Suspense fallback={null}>
          <WhatsAppButton />
        </Suspense>
      )}
    </div>
  );
};

// Redirect authenticated users from Home to their appropriate dashboard
const HomeOrRedirect = () => {
  const { isAuthenticated, user, loading } = useAuth();
  const portalMode = getPortalMode();
  
  // Show nothing while checking auth status
  if (loading) {
    return null;
  }
  
  // If authenticated, redirect to appropriate dashboard
  if (isAuthenticated) {
    if (portalMode === 'admin') {
      const destination = ['admin', 'staff'].includes(user?.role) ? '/admin' : '/admin/login';
      return <Navigate to={destination} replace />;
    }

    const destination = ['admin', 'staff'].includes(user?.role) ? '/login' : '/dashboard';
    return <Navigate to={destination} replace />;
  }

  if (portalMode === 'admin') {
    return <Navigate to="/admin/login" replace />;
  }
  
  // If not authenticated, show home page
  return <Home />;
};

function App() {
  const portalMode = getPortalMode();
  const allowCustomerRoutes = portalMode !== 'admin';
  const allowAdminRoutes = portalMode !== 'customer';

  return (
    <ThemeProvider>
      <SettingsProvider>
        <ErrorBoundary>
          <Router>
          <AuthProvider>
            <SocketProvider>
              <WishlistProvider>
                <ScrollToTop />
                <ThemeApplier />
                <AppLayout>
              <Suspense fallback={<RouteLoader />}>
              <Routes>
              {/* Public Landing */}
              <Route path="/" element={<HomeOrRedirect />} />

              {allowCustomerRoutes && (
                <>
                  <Route path="/login" element={<Login />} />
                  <Route path="/signup" element={<Signup />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password/:token" element={<ResetPassword />} />

                  {/* Public customer routes */}
                  <Route path="/shop" element={<Shop />} />
                  <Route path="/services/:slug" element={<ServiceDetail />} />

                  {/* Protected customer routes */}
                  <Route
                    path="/plan"
                    element={
                      <ProtectedRoute>
                        <Plan />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    }
                  />

                  {/* Payment Routes */}
                  <Route
                    path="/payment/:orderId"
                    element={
                      <ProtectedRoute>
                        <PaymentPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/payment/guest/:token" element={<GuestPaymentPage />} />
                  <Route
                    path="/payment/callback/:provider"
                    element={
                      <ProtectedRoute>
                        <PaymentCallback />
                      </ProtectedRoute>
                    }
                  />

                  {/* Notifications Route */}
                  <Route
                    path="/notifications"
                    element={
                      <ProtectedRoute>
                        <NotificationsPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Wishlist Route - Redirect to Dashboard wishlist tab */}
                  <Route
                    path="/wishlist"
                    element={
                      <ProtectedRoute>
                        <Navigate to="/dashboard?tab=wishlist" replace />
                      </ProtectedRoute>
                    }
                  />
                </>
              )}

              {allowAdminRoutes && (
                <>
                  <Route path="/admin/login" element={<Login />} />

                  {/* Admin Routes - Require Admin Role */}
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute requireAdmin>
                        <AdminDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/users"
                    element={
                      <ProtectedRoute requireAdmin>
                        <UsersManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/projects"
                    element={
                      <ProtectedRoute requireAdmin>
                        <ProjectsManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/quotes"
                    element={
                      <ProtectedRoute requireAdmin>
                        <QuotesManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/orders"
                    element={
                      <ProtectedRoute requireAdmin>
                        <OrdersManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/products"
                    element={
                      <ProtectedRoute requireAdmin>
                        <ProductsManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/inventory"
                    element={
                      <ProtectedRoute requireAdmin>
                        <InventoryManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/categories"
                    element={
                      <ProtectedRoute requireAdmin>
                        <CategoriesManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/banners"
                    element={
                      <ProtectedRoute requireAdmin>
                        <BannersManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/analytics"
                    element={
                      <ProtectedRoute requireAdmin>
                        <Analytics />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/consultations"
                    element={
                      <ProtectedRoute requireAdmin>
                        <ConsultationsManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/settings"
                    element={
                      <ProtectedRoute requireAdmin>
                        <SettingsManagement />
                      </ProtectedRoute>
                    }
                  />
                </>
              )}

              <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
            </AppLayout>
              </WishlistProvider>
            </SocketProvider>
          </AuthProvider>
          </Router>
        </ErrorBoundary>
      </SettingsProvider>
    </ThemeProvider>
  );
}

export default App;
